import type { ReDubSelectionRequest } from '@src/components/transcript/hooks/useSpanReplace';

/**
 * PLAT-12047 / PLAT-12056 — an action asked for on a host's own transcript
 * (the Transcript & Languages tab, a clip's transcript), carried into
 * Translation Review so the per-line work happens there: the review finds
 * the words in its own column and opens the Redub Selection popover on them
 * (`redub`), or the Fix Translation editor over them (`fix`).
 *
 * No DOM anchor: the host transcript's words are hidden once the review is
 * up, so the review anchors on its own copy. `nonce` tells one hand-off from
 * the next, and the host drops the object once the review reports it
 * consumed, so a reload or a shared link never replays it.
 */
export interface RedubHandoff {
  kind: 'redub';
  segmentIds: string[];
  speakerLabels: string[];
  translatedText: string;
  sourceText: string;
  nonce: number;
}

export interface FixHandoff {
  kind: 'fix';
  /** The selection's covered source indices: what the editor opens over. */
  coveredIndices: number[];
  /** The lines those words belong to, for finding them in the review. */
  segmentIds: string[];
  nonce: number;
}

export type ReviewHandoff = RedubHandoff | FixHandoff;

/** What the host's transcript sends up when a menu row hands off. */
export type ReviewHandoffRequest =
  | ({ kind: 'redub' } & ReDubSelectionRequest)
  | { kind: 'fix'; coveredIndices: number[]; segmentIds: string[] };

/**
 * The host's hand-off, as the transcript receives it: where to send a
 * request, and whether a re-dub is on offer for this language (a fix always
 * is; a re-dub needs a dub to re-record).
 */
export interface ReviewHandoffProp {
  request: (request: ReviewHandoffRequest) => void;
  redub: boolean;
}

export function redubHandoffFrom(
  request: ReDubSelectionRequest,
  nonce: number,
): RedubHandoff {
  return {
    kind: 'redub',
    segmentIds: request.segmentIds,
    speakerLabels: request.speakerLabels,
    translatedText: request.translatedText,
    sourceText: request.sourceText,
    nonce,
  };
}

export function fixHandoffFrom(
  request: { coveredIndices: number[]; segmentIds: string[] },
  nonce: number,
): FixHandoff {
  return {
    kind: 'fix',
    coveredIndices: request.coveredIndices,
    segmentIds: request.segmentIds,
    nonce,
  };
}

export function reviewHandoffFrom(
  request: ReviewHandoffRequest,
  nonce: number,
): ReviewHandoff {
  return request.kind === 'fix'
    ? fixHandoffFrom(request, nonce)
    : redubHandoffFrom(request, nonce);
}

/** How long the review waits for the words to render before giving up on them. */
export const REVIEW_HANDOFF_WAIT_MS = 4000;
